import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RefreshCw, ShieldX, Search } from "lucide-react";

/**
 * Public Revocation Register — /verify/revocation-register
 * Machine + human-readable list of every revoked / suspended certificate.
 */
export default function RevocationRegister() {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const [rows, setRows] = useState([]);
  const [country, setCountry] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const url = `${base}/api/public/revocation-register?limit=200${country ? "&country=" + encodeURIComponent(country) : ""}`;
      const r = await fetch(url);
      const d = await r.json();
      setRows(d.items || []);
    } catch {} finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country]);

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="kicker">Public · Trust Ledger</div>
          <h1 className="display text-4xl">Certificate Revocation Register</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every certificate whose status is <strong>revoked</strong> or <strong>suspended</strong>. Banks, insurers, courts and regulators may consult this register to confirm that a previously-issued certificate is no longer in force.
          </p>
        </div>
        <div className="flex gap-2 mb-4" data-testid="rev-filter">
          <Input placeholder="Filter by country (e.g. Zimbabwe)" value={country} onChange={(e) => setCountry(e.target.value)} className="max-w-xs" data-testid="rev-country" />
          <Button size="sm" variant="outline" onClick={load} disabled={busy}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
          <a href={`${base}/api/public/revocation-register${country ? "?country=" + encodeURIComponent(country) : ""}`} target="_blank" rel="noreferrer">
            <Button size="sm" variant="ghost">Raw JSON →</Button>
          </a>
        </div>

        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-2.5">Certificate</th><th className="p-2.5">Status</th>
                <th className="p-2.5">Asset</th><th className="p-2.5">Country</th>
                <th className="p-2.5">Since</th><th className="p-2.5">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r) => (
                <tr key={r.certificate_number} data-testid={`rev-row-${r.certificate_number}`}>
                  <td className="p-2.5 mono text-[11px]"><Link to={`/verify/certificate/${r.certificate_number}`} className="underline text-primary">{r.certificate_number}</Link></td>
                  <td className="p-2.5"><span className={`mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm ${r.status === "revoked" ? "bg-red-500/10 text-red-700" : "bg-amber-500/10 text-amber-700"}`}>{r.status}</span></td>
                  <td className="p-2.5">{r.asset_type} · {r.asset_ref || r.asset_id}</td>
                  <td className="p-2.5">{r.country}</td>
                  <td className="p-2.5 mono text-[10.5px] text-muted-foreground">{(r.revoked_at || r.updated_at || "").slice(0, 10)}</td>
                  <td className="p-2.5 text-[11.5px] text-muted-foreground max-w-[280px] truncate" title={r.revocation_reason || r.suspension_reason}>{r.revocation_reason || r.suspension_reason || "—"}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground text-sm"><ShieldX className="h-6 w-6 mx-auto mb-2 opacity-60" />No revocations in this scope.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}
